import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, VStack } from "@chakra-ui/react";
import { AnalystProto, ColumnScopeProto, DataScopeProto } from "build-ts/analyst";
import { ErrorDetailProto, FilterProto, Header, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { BenchmarkEntityProto, KGColumnProto } from "build-ts/knowledege_graph";
import { ColumnType, FilterOperator, MetadataTypeEnum, MetricEdgeType } from "build-ts/enums";
import { KGObservationProto } from "build-ts/analysis_plan";
import { getAllMetrics, listMetrics } from "services/MetricService";
import { fetchMetadataHeaders } from "services/MetadataServices";
import { getKGObservationData } from "services/AnalysisTreeServices";
import { getKGColumnById } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import DataScopeLeftPanel from "components/analyst/DataScopeLeftPanel";
import DataScopeRightPanel from "components/analyst/DataScopeRightPanel";

type EntityKind = "metric" | "measure" | "attribute" | "time" | "benchmark";

// ─── Props ────────────────────────────────────────────────────────────────────
interface AnalystDataScopeContainerProps {
    analyst: AnalystProto | undefined;
    setAnalyst: React.Dispatch<React.SetStateAction<AnalystProto | undefined>>;
    isCreatingNew?: boolean;
    hasDiscardedChanges?: boolean;
    setHasDiscardedChanges?: React.Dispatch<React.SetStateAction<boolean>>;
    originalAnalyst?: AnalystProto;
    onAttributesNeedRefreshChange?: (value: boolean) => void;
    onScopeLockedChange?: (locked: boolean) => void;
    refreshAttributeValuesRef?: React.MutableRefObject<(() => Promise<void>) | null>;
    analystSavedAt?: number;
    onTriggerAnalystSave?: (updatedDataScope: DataScopeProto) => Promise<void>;
}

const AnalystDataScopeContainer: React.FC<AnalystDataScopeContainerProps> = ({
    analyst,
    setAnalyst,
    isCreatingNew = false,
    hasDiscardedChanges = false,
    setHasDiscardedChanges,
    originalAnalyst,
    onAttributesNeedRefreshChange,
    onScopeLockedChange,
    refreshAttributeValuesRef,
    analystSavedAt,
    onTriggerAnalystSave,
}) => {
    // ─── Selected metrics (with full edges) ──────────────────────────────────────
    const [selectedMetricsWithEdges, setSelectedMetricsWithEdges] = useState<MetricProto[]>([]);

    // ─── Selected related entities ────────────────────────────────────────────────
    const [selectedBenchmarks, setSelectedBenchmarks] = useState<Header[]>([]);
    const [columnScopes, setColumnScopes] = useState<ColumnScopeProto[]>([]);

    // ─── Available entity lists (union from selected metrics) ────────────────────
    const [listOfMetrics, setListOfMetrics] = useState<Header[]>([]);
    const [benchmarkList, setBenchmarkList] = useState<Header[]>([]);
    const [measureList, setMeasureList] = useState<Header[]>([]);
    const [timeframeList, setTimeframeList] = useState<Header[]>([]);
    const [attributeList, setAttributeList] = useState<Header[]>([]);

    // ─── Loading states ───────────────────────────────────────────────────────────
    const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(true);
    const [isLoadingDataScope, setIsLoadingDataScope] = useState<boolean>(false);
    // True while fetching edges for the analyst's pre-selected metrics (stops Related Entities skeleton)
    const [isLoadingRelatedEntities, setIsLoadingRelatedEntities] = useState<boolean>(false);

    // ─── Data model list and full metric proto map (keyed by metric ID) ──────────
    const [dataModelList, setDataModelList] = useState<Header[]>([]);
    const [metricProtoMap, setMetricProtoMap] = useState<Map<string, MetricProto>>(new Map());
    // ─── KGColumn Map (keyed by KG column ID) ──────────────────────────────────
    const [kgColumnProtoMap, setKgColumnProtoMap] = useState<Map<string, KGColumnProto>>(new Map());

    // ─── Session-new tracking (green border on newly added cards) ─────────────────
    // Tracks IDs added since the last analyst load or discard. Cleared automatically
    // when the user switches analysts, creates a new analyst, or discards changes.
    const [sessionNewIds, setSessionNewIds] = useState<Set<string>>(new Set());
    const markNew = (id: string) => setSessionNewIds(prev => prev.has(id) ? prev : new Set(prev).add(id));
    const unmarkNew = (id: string) => setSessionNewIds(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; });
    const resetNewIds = () => setSessionNewIds(new Set());

    // Clear green borders after every successful save (create or update)
    useEffect(() => {
        if (!analystSavedAt) return;
        resetNewIds();
    }, [analystSavedAt]);

    // ─── Refs for sync tracking ───────────────────────────────────────────────
    const lastLoadedAnalystIdRef = useRef<string | undefined>(undefined);
    const isInitialSyncRef = useRef<boolean>(true);
    // Tracks IDs currently being fetched so we never issue duplicate requests
    const fetchingKgColumnIdsRef = useRef<Set<string>>(new Set());
    // Tracks metric IDs for which edges have already been fetched (cache guard)
    const fetchedEdgesForMetricIdsRef = useRef<Set<string>>(new Set());

    // ─── Derived: selected metric headers ────────────────────────────────────────
    const selectedMetricHeaders = useMemo(
        () => selectedMetricsWithEdges.map(m => m.header).filter((h): h is Header => h !== undefined),
        [selectedMetricsWithEdges]
    );

    // ─── Derived: selected measures, timeframes, attributes from columnScopes ─────
    const selectedMeasures = useMemo(
        () => columnScopes
            .filter(f => f?.kg_column?.header?.column_type === ColumnType.MEASURE)
            .map(f => f?.kg_column?.header)
            .filter((h): h is Header => h !== undefined),
        [columnScopes]
    );

    const selectedTimeframes = useMemo(
        () => columnScopes
            .filter(f => f?.kg_column?.header?.column_type === ColumnType.TEMPORAL)
            .map(f => f?.kg_column?.header)
            .filter((h): h is Header => h !== undefined),
        [columnScopes]
    );

    const selectedAttributes = useMemo(
        () => columnScopes
            .filter(f => f?.kg_column?.header?.column_type === ColumnType.ATTRIBUTE)
            .map(f => f?.kg_column?.header)
            .filter((h): h is Header => h !== undefined),
        [columnScopes]
    );

    // ─── Initialize loading states on analyst change ──────────────────────────
    useEffect(() => {
        if (isCreatingNew || !analyst?.header?.id) {
            setIsLoadingRelatedEntities(false);
            setIsLoadingDataScope(false);
            return;
        }
        const hasMetrics = (analyst?.data_scope?.metrics?.length ?? 0) > 0;
        setIsLoadingRelatedEntities(hasMetrics);
        setIsLoadingDataScope(hasMetrics);
    }, [analyst?.header?.id, isCreatingNew]);

    // ─── Load analyst data scope on analyst change ────────────────────────────
    useEffect(() => {
        const loadDataScope = async () => {
            if (isLoadingMetrics) return;
            const currentAnalystId = analyst?.header?.id;
            isInitialSyncRef.current = true;

            if (isCreatingNew) {
                lastLoadedAnalystIdRef.current = undefined;
                setSelectedMetricsWithEdges([]);
                setSelectedBenchmarks([]);
                setColumnScopes([]);
                resetNewIds();
                isInitialSyncRef.current = false;
                return;
            }

            if (currentAnalystId && currentAnalystId !== lastLoadedAnalystIdRef.current && !hasDiscardedChanges) {
                lastLoadedAnalystIdRef.current = currentAnalystId;
                resetNewIds();

                if (analyst?.data_scope) {
                    const metricsFromDataScope = analyst.data_scope.metrics || [];
                    if (metricsFromDataScope.length > 0) {
                        try {
                            setIsLoadingDataScope(true);
                            const metricIds = metricsFromDataScope.map(m => m.header?.id).filter((id): id is string => !!id);
                            if (metricIds.length > 0) {
                                const metricsWithEdges = await getAllMetrics(metricIds, true);
                                setSelectedMetricsWithEdges(metricsWithEdges || []);
                            }
                        } catch (error) {
                            console.error("Error fetching metrics with edges:", error);
                            showToast("Failed to load metric edges", "", "error");
                            setSelectedMetricsWithEdges(metricsFromDataScope);
                        } finally {
                            setIsLoadingDataScope(false);
                            setIsLoadingRelatedEntities(false);
                        }
                    } else {
                        setSelectedMetricsWithEdges([]);
                        setIsLoadingRelatedEntities(false);
                        setIsLoadingDataScope(false);
                    }

                    const benchmarkHeaders = analyst.data_scope.benchmarks
                        ?.map(b => b.header)
                        .filter((h): h is Header => h !== undefined) || [];
                    setSelectedBenchmarks(benchmarkHeaders);
                    setColumnScopes(analyst.data_scope.column_scopes || []);
                } else {
                    setSelectedMetricsWithEdges([]);
                    setSelectedBenchmarks([]);
                    setColumnScopes([]);
                }
            }

            isInitialSyncRef.current = false;
        };

        loadDataScope();
    }, [analyst?.header?.id, isCreatingNew, isLoadingMetrics]);

    // ─── Reload original data on discard ─────────────────────────────────────────
    useEffect(() => {
        const loadOriginalData = async () => {
            if (isLoadingMetrics) return;

            if (!originalAnalyst || !hasDiscardedChanges) return;
            resetNewIds();

            if (originalAnalyst?.data_scope) {
                const metricsFromDataScope = originalAnalyst.data_scope.metrics || [];
                if (metricsFromDataScope.length > 0) {
                    try {
                        setIsLoadingDataScope(true);
                        const metricIds = metricsFromDataScope.map(m => m.header?.id).filter((id): id is string => !!id);
                        if (metricIds.length > 0) {
                            const metricsWithEdges = await getAllMetrics(metricIds, true);
                            setSelectedMetricsWithEdges(metricsWithEdges || []);
                        }
                    } catch (error) {
                        console.error("Error fetching metrics with edges:", error);
                        showToast("Failed to load metric edges", "", "error");
                        setSelectedMetricsWithEdges(metricsFromDataScope);
                    } finally {
                        setIsLoadingDataScope(false);
                        setIsLoadingRelatedEntities(false);
                    }
                } else {
                    setSelectedMetricsWithEdges([]);
                    setIsLoadingRelatedEntities(false);
                    setIsLoadingDataScope(false);
                }

                const benchmarkHeaders = originalAnalyst.data_scope.benchmarks
                    ?.map(b => b.header)
                    .filter((h): h is Header => h !== undefined) || [];
                setSelectedBenchmarks(benchmarkHeaders);
                setColumnScopes(originalAnalyst.data_scope.column_scopes || []);
            } else {
                setSelectedMetricsWithEdges([]);
                setSelectedBenchmarks([]);
                setColumnScopes([]);
            }

            setHasDiscardedChanges?.(false);
        };

        loadOriginalData();
    }, [hasDiscardedChanges, isLoadingMetrics]);

    // ─── Fetch metric headers list on mount ───────────────── 
    useEffect(() => {
        fetchMetricsHeaders();
        fetchDataModels();
    }, []);

    // ─── Sync data scope changes up to parent analyst ─────────────────────────────
    const dataScope = useMemo((): DataScopeProto => {
        const benchmarks = selectedBenchmarks.map(b =>
            BenchmarkEntityProto.create({ header: b, benchmark_type: b.benchmark_type })
        );
        return DataScopeProto.create({
            use_default_scope: false,
            metrics: selectedMetricsWithEdges,
            benchmarks,
            column_scopes: columnScopes,
        });
    }, [selectedMetricsWithEdges, selectedBenchmarks, columnScopes]);

    useEffect(() => {
        if (isInitialSyncRef.current) return;
        if (isLoadingDataScope) return;
        if (analyst) {
            setAnalyst({ ...analyst, data_scope: dataScope });
        } else {
            setAnalyst(AnalystProto.create({
                header: {
                    name: "",
                },
                data_scope: dataScope,
                role: "",
                instructions: "",
                notes: "",
            }) as AnalystProto);
        }
    }, [dataScope]);


    const fetchMetricsHeaders = async () => {
        // fetch headers and stop the Metrics skeleton as soon as the list arrives
        let fetchedMetrics: Header[] = [];
        try {
            setIsLoadingMetrics(true);
            const metrics = await listMetrics("");
            fetchedMetrics = metrics || [];
            setListOfMetrics(fetchedMetrics);
        } catch {
            showToast("Failed to fetch Metrics", "", "error");
        } finally {
            setIsLoadingMetrics(false);
        }

        // Fetch all metrics without edges for tooltip / display data.
        if (fetchedMetrics.length > 0) {
            const ids = fetchedMetrics.map((m: Header) => m.id).filter((id: string | undefined): id is string => !!id);
            const allMetrics = await getAllMetrics(ids, false);
            setMetricProtoMap(prev => {
                const next = new Map(prev);
                allMetrics.forEach((m: MetricProto) => {
                    if (m.header?.id && !fetchedEdgesForMetricIdsRef.current.has(m.header.id)) {
                        next.set(m.header.id, m);
                    }
                });
                return next;
            });
        }
    };

    const fetchDataModels = async () => {
        try {
            const result = await fetchMetadataHeaders(MetadataTypeEnum.DATA_MODEL, -1, 1);
            setDataModelList(result?.headers || []);
        } catch {
            showToast("Failed to fetch Data Models", "", "error");
        }
    };

    // ─── Build union lists from selected metrics ──────────────────────────────────
    useEffect(() => {
        if (selectedMetricsWithEdges.length === 0) {
            setBenchmarkList([]);
            setMeasureList([]);
            setTimeframeList([]);
            setAttributeList([]);
            setSelectedBenchmarks([]);
            setColumnScopes([]);
            return;
        }

        const allHeadersMap = new Map<string, Header>();
        selectedMetricsWithEdges.forEach(metric => {
            metric.edges?.forEach(edge => {
                if (edge.header?.id && (
                    edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE ||
                    edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE ||
                    edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE ||
                    edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE
                )) {
                    allHeadersMap.set(edge.header.id, edge.header);
                }
            });
        });

        const allHeaders = Array.from(allHeadersMap.values());
        const newBenchmarkList = allHeaders.filter(h => h.type === MetadataTypeEnum.BENCHMARK);
        const newMeasureList = allHeaders.filter(h => h.column_type === ColumnType.MEASURE);
        const newTimeframeList = allHeaders.filter(h => h.column_type === ColumnType.TEMPORAL);
        const newAttributeList = allHeaders.filter(h => h.column_type === ColumnType.ATTRIBUTE);

        setBenchmarkList(newBenchmarkList);
        setMeasureList(newMeasureList);
        setTimeframeList(newTimeframeList);
        setAttributeList(newAttributeList);

        setSelectedBenchmarks(prev => prev.filter(h => newBenchmarkList.some(item => item.id === h.id)));
        setColumnScopes(prev => prev.filter(f => {
            const header = f?.kg_column?.header;
            if (!header?.id) return true;
            if (header.column_type === ColumnType.MEASURE) return newMeasureList.some(m => m.id === header.id);
            if (header.column_type === ColumnType.TEMPORAL) return newTimeframeList.some(t => t.id === header.id);
            if (header.column_type === ColumnType.ATTRIBUTE) return newAttributeList.some(a => a.id === header.id);
            return true;
        }));
    }, [selectedMetricsWithEdges]);

    // ─── Pre-fetch full KGColumnProto for all available KG columns ───────────────
    // Runs whenever the available measure/attribute/timeframe lists change (whenever selected metrics change).
    useEffect(() => {
        const allIds = [...measureList, ...attributeList, ...timeframeList]
            .map(h => h.id)
            .filter((id): id is string => !!id && !fetchingKgColumnIdsRef.current.has(id));

        if (allIds.length === 0) return;

        allIds.forEach(id => fetchingKgColumnIdsRef.current.add(id));

        Promise.all(allIds.map(id => getKGColumnById(id, true)))
            .then(results => {
                setKgColumnProtoMap(prev => {
                    const next = new Map(prev);
                    results.forEach((col, i) => {
                        if (col) next.set(allIds[i], col);
                    });
                    return next;
                });
            })
            .catch(() => {
                // Allow retry on next list change by releasing the IDs
                allIds.forEach(id => fetchingKgColumnIdsRef.current.delete(id));
            });
    }, [measureList, attributeList, timeframeList]);

    // ─── Metric toggle handlers ───────────────────────────────────────────────────
    const toggleMetricSelection = async (metric: Header) => {
        if (!metric.id) return;
        const isSelected = selectedMetricHeaders.some(item => item.id === metric.id);
        if (isSelected) {
            unmarkNew(metric.id);
            setSelectedMetricsWithEdges(prev => prev.filter(m => m.header?.id !== metric.id));
            return;
        }

        // edges already fetched — add directly
        if (fetchedEdgesForMetricIdsRef.current.has(metric.id)) {
            const cached = metricProtoMap.get(metric.id);
            if (cached) {
                markNew(metric.id);
                setSelectedMetricsWithEdges(prev => [...prev, cached]);
                return;
            }
        }

        // Fetch with edges, then add
        try {
            const results = await getAllMetrics([metric.id], true);
            const metricWithEdges = results[0];
            if (!metricWithEdges) return;
            fetchedEdgesForMetricIdsRef.current.add(metric.id);
            setMetricProtoMap(prev => new Map(prev).set(metric.id!, metricWithEdges));
            markNew(metric.id);
            setSelectedMetricsWithEdges(prev => [...prev, metricWithEdges]);
        } catch {
            showToast("Failed to fetch Metric", "", "error");
        }
    };

    const removeSelectedMetric = (metricId?: string) => {
        if (!metricId) return;
        unmarkNew(metricId);
        setSelectedMetricsWithEdges(prev => prev.filter(m => m.header?.id !== metricId));
    };

    // ─── Related entity toggle handlers ──────────────────────────────────────────
    const toggleBenchmarkSelection = (header: Header) => {
        if (!header.id) return;
        const isSelected = selectedBenchmarks.some(item => item.id === header.id);
        if (isSelected) {
            unmarkNew(header.id);
            setSelectedBenchmarks(prev => prev.filter(item => item.id !== header.id));
        } else {
            markNew(header.id);
            setSelectedBenchmarks(prev => [...prev, header]);
        }
    };

    const toggleMeasureSelection = (header: Header) => {
        if (!header.id) return;
        const isSelected = selectedMeasures.some(m => m.id === header.id);
        if (isSelected) {
            unmarkNew(header.id);
            setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
        } else {
            markNew(header.id);
            setColumnScopes(prev => [...prev, ColumnScopeProto.create({ kg_column: KGColumnProto.create({ header }) })]);
        }
    };

    const toggleTimeframeSelection = (header: Header) => {
        if (!header.id) return;
        const isSelected = selectedTimeframes.some(t => t.id === header.id);
        if (isSelected) {
            unmarkNew(header.id);
            setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
        } else {
            markNew(header.id);
            setColumnScopes(prev => [...prev, ColumnScopeProto.create({ kg_column: KGColumnProto.create({ header }) })]);
        }
    };

    const [loadingAttributeIds, setLoadingAttributeIds] = useState<Set<string>>(new Set());
    // attributeId → errors from the last failed fetch attempt
    const [attributeValueErrors, setAttributeValueErrors] = useState<Map<string, ErrorDetailProto[]>>(new Map());

    // ─── Attribute refresh tracking ───────────────────────────────────────────
    const [attributesNeedRefresh, setAttributesNeedRefresh] = useState<boolean>(false);
    const [isRefreshingAttributeValues, setIsRefreshingAttributeValues] = useState<boolean>(false);
    // True during the entire constraint-save sequence (refresh + analyst update)
    const [isAutoSavingConstraint, setIsAutoSavingConstraint] = useState<boolean>(false);

    // Notify parent whenever flag changes
    useEffect(() => {
        onAttributesNeedRefreshChange?.(attributesNeedRefresh);
    }, [attributesNeedRefresh]);

    // Bubble lock state (refresh API call in-flight) to parent
    useEffect(() => {
        onScopeLockedChange?.(isRefreshingAttributeValues || isAutoSavingConstraint);
    }, [isRefreshingAttributeValues, isAutoSavingConstraint]);

    const toggleAttributeSelection = async (header: Header) => {
        if (!header.id) return;
        const isSelected = selectedAttributes.some(a => a.id === header.id);
        if (isSelected) {
            unmarkNew(header.id);
            setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
            setLoadingAttributeIds(prev => { const next = new Set(prev); next.delete(header.id!); return next; });
            // If other attributes remain, their available values may now be stale
            const remainingAttrCount = selectedAttributes.filter(a => a.id !== header.id).length;
            if (remainingAttrCount > 0) setAttributesNeedRefresh(true);
            return;
        }

        markNew(header.id);
        // Mark as loading so the editor shows a spinner instead of "no values"
        setLoadingAttributeIds(prev => new Set(prev).add(header.id!));

        // Add scope immediately with just the header for instant UI feedback
        setColumnScopes(prev => [
            ...prev,
            ColumnScopeProto.create({
                kg_column: KGColumnProto.create({ header }),
                operator: FilterOperator.NO_FILTER_OP,
                values: [],
                is_hidden: false,
                available_values: [],
            })]);

        try {
            // Use pre-fetched cache when available; fall back to a direct API call
            const cachedKgColumn = kgColumnProtoMap.get(header.id);
            const kgColumn = cachedKgColumn ?? await getKGColumnById(header.id, true);
            if (!kgColumn) return;

            // Store in cache if it wasn't there yet
            if (cachedKgColumn !== undefined) {
                setKgColumnProtoMap(prev => new Map(prev).set(header.id!, kgColumn));
            }

            let availableVals: string[] = [];
            if (kgColumn.cardinality === 1) {
                // Low cardinality — auto-fetch distinct values
                const scopeForFetch = ColumnScopeProto.create({ kg_column: kgColumn, values: [], available_values: [] });
                const { vals, errors } = await fetchAttributeValues(scopeForFetch, -1);
                if (vals !== null) {
                    availableVals = vals;
                    // Clear any previous error for this attribute
                    setAttributeValueErrors(prev => { const next = new Map(prev); next.delete(header.id!); return next; });
                } else {
                    setAttributeValueErrors(prev => new Map(prev).set(header.id!, errors));
                }
            }

            // Replace the scope with full KGColumnProto + available_values (even on error, we
            // still store the kgColumn so cardinality is known for the "Too many values" pill)
            setColumnScopes(prev => prev.map(f => {
                if (f?.kg_column?.header?.id !== header.id) return f;
                return ColumnScopeProto.create({
                    kg_column: kgColumn,
                    operator: f.operator,
                    values: f.values,
                    is_hidden: f.is_hidden,
                    available_values: availableVals,
                });
            }));
        } finally {
            setLoadingAttributeIds(prev => { const next = new Set(prev); next.delete(header.id!); return next; });
        }
    };

    // ─── Attribute constraint handlers ───────────────────────────────────────

    // Returns { vals, errors }.
    // On any failure (network/HTTP error or server-side error_details), vals is null and
    // errors carries the details — the caller must NOT update the scope in this case.
    const fetchAttributeValues = async (
        scope: ColumnScopeProto,
        scopeIndex: number,
        scopesOverride?: ColumnScopeProto[],
    ): Promise<{ vals: string[] | null; errors: ErrorDetailProto[] }> => {
        if (!scope.kg_column) return { vals: [], errors: [] };

        const scopes = scopesOverride ?? columnScopes;
        const otherActiveFilters = scopes
            .filter((f, i) => i !== scopeIndex && f.operator !== undefined && f.values && f.values.length > 0)
            .map(f => FilterProto.create({
                filter_id: f.kg_column?.header?.id,
                kg_column_header: f.kg_column?.header,
                operator: f.operator,
                values: f.values,
                is_hidden: false,
            }));

        try {
            const request = KGObservationProto.create({
                metrics: selectedMetricsWithEdges,
                columns: [ObservationColumnProto.create({
                    column_id: scope.kg_column.header?.id,
                    kg_column_header: scope.kg_column.header,
                })],
                filters: otherActiveFilters,
                calculate_non_measure_only: true,
            });
            const response = await getKGObservationData([request]);

            // API-level failure (HTTP error / network error)
            if (!response) {
                return {
                    vals: null,
                    errors: [{ error_name: "Fetch failed", error_msg: "Could not load attribute values. Please try again." }],
                };
            }

            const first = response[0];
            // Server returned explicit error details
            if (first?.error_details?.length) {
                return { vals: null, errors: first.error_details };
            }

            const raw = first?.kg_observation_data?.non_measure_values[0]?.data_value_json_array;
            return { vals: JSON.parse(raw || "[]").map(String), errors: [] };
        } catch {
            return {
                vals: null,
                errors: [{ error_name: "Unexpected error", error_msg: "An unexpected error occurred while loading attribute values." }],
            };
        }
    };

    const onSaveConstraint = async (scopeIndex: number, operator: FilterOperator, values: string[]) => {
        // Build the new scopes synchronously so we can pass them to the refresh without relying on
        // stale closure state after setColumnScopes (which is batched/async in React).
        const existing = columnScopes[scopeIndex];
        const newScopes = columnScopes.map((s, i) =>
            i === scopeIndex
                ? ColumnScopeProto.create({
                    kg_column: existing.kg_column,
                    operator,
                    values,
                    is_hidden: existing.is_hidden,
                    available_values: existing.available_values,
                })
                : s
        );

        // If there is no auto-save callback (create flow), just update state normally
        if (!onTriggerAnalystSave) {
            setColumnScopes(newScopes);
            if (selectedAttributes.length > 1) setAttributesNeedRefresh(true);
            return;
        }

        // ── Auto-save flow (update flow only) ────────────────────────────────
        setIsAutoSavingConstraint(true);
        try {
            let finalScopes = newScopes;
            setColumnScopes(newScopes);

            // Refresh available_values for other attributes using the freshly built scopes
            if (selectedAttributes.length > 1) {
                finalScopes = await refreshAttributeValuesWithScopes(newScopes);
            } else {
                setAttributesNeedRefresh(false);
            }

            // Build an up-to-date DataScopeProto with the refreshed scopes and hand it
            // to AnalystPage so it can call updateAnalyst without reading stale state.
            const benchmarks = selectedBenchmarks.map(b =>
                BenchmarkEntityProto.create({ header: b, benchmark_type: b.benchmark_type })
            );
            const updatedDataScope = DataScopeProto.create({
                use_default_scope: false,
                metrics: selectedMetricsWithEdges,
                benchmarks,
                column_scopes: finalScopes,
            });
            await onTriggerAnalystSave(updatedDataScope);
        } finally {
            setIsAutoSavingConstraint(false);
        }
    };

    const removeColumnScope = (header: Header, kind: EntityKind) => {
        if (!header.id) return;
        unmarkNew(header.id);
        setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));

        if (kind === "attribute") {
            const scope = columnScopes.find(f => f?.kg_column?.header?.id === header.id);
            const hadConstraint = scope
                ? (scope.operator !== undefined &&
                    scope.operator !== FilterOperator.NO_FILTER_OP &&
                    (scope.values?.length ?? 0) > 0)
                : false;
            setLoadingAttributeIds(prev => { const next = new Set(prev); next.delete(header.id!); return next; });
            setAttributeValueErrors(prev => { const next = new Map(prev); next.delete(header.id!); return next; });
            if (hadConstraint) setAttributesNeedRefresh(true);
        }
    };

    // ─── Refresh all attribute available_values ───────────────────────────────

    // Used by the main save-button fallback path via refreshAttributeValuesRef.
    // Reads columnScopes from the current closure.
    const onRefreshAttributeValues = async () => {
        const attrScopes = columnScopes.filter(
            s => s.kg_column?.header?.column_type === ColumnType.ATTRIBUTE &&
                s.kg_column?.cardinality && s.kg_column.cardinality === 1
        );
        if (attrScopes.length === 0) return;

        setIsRefreshingAttributeValues(true);
        try {
            const results = await Promise.all(
                attrScopes.map(scope => {
                    const scopeIndex = columnScopes.findIndex(
                        f => f?.kg_column?.header?.id === scope.kg_column?.header?.id
                    );
                    return fetchAttributeValues(scope, scopeIndex).then(result => ({
                        id: scope.kg_column?.header?.id,
                        ...result,
                    }));
                })
            );

            // Accumulate errors and only update scopes that succeeded
            const newErrors = new Map<string, ErrorDetailProto[]>();
            setColumnScopes(prev =>
                prev.map(f => {
                    const result = results.find(r => r.id === f.kg_column?.header?.id);
                    if (!result) return f;
                    if (result.vals === null) {
                        // Keep existing scope unchanged; record the error
                        if (result.id) newErrors.set(result.id, result.errors);
                        return f;
                    }
                    // Success — clear any previous error for this attribute
                    const newAvailable = result.vals;
                    const cleanedValues = (f.values ?? []).filter(v => newAvailable.includes(v));
                    return ColumnScopeProto.create({
                        kg_column: f.kg_column,
                        operator: f.operator,
                        values: cleanedValues,
                        is_hidden: f.is_hidden,
                        available_values: newAvailable,
                    });
                })
            );

            setAttributeValueErrors(prev => {
                const next = new Map(prev);
                // Clear errors for attributes that succeeded; set errors for those that failed
                results.forEach(r => {
                    if (!r.id) return;
                    if (r.vals === null) next.set(r.id, r.errors);
                    else next.delete(r.id);
                });
                return next;
            });

            setAttributesNeedRefresh(false);
        } finally {
            setIsRefreshingAttributeValues(false);
        }
    };

    // Used by the constraint-save auto-flow. Accepts explicit scopes so we avoid
    // reading stale closure state right after a setColumnScopes call.
    // Returns the final refreshed scopes so the caller can build an up-to-date DataScopeProto.
    const refreshAttributeValuesWithScopes = async (scopes: ColumnScopeProto[]): Promise<ColumnScopeProto[]> => {
        const attrScopes = scopes.filter(
            s => s.kg_column?.header?.column_type === ColumnType.ATTRIBUTE &&
                s.kg_column?.cardinality && s.kg_column.cardinality === 1
        );
        if (attrScopes.length === 0) return scopes;

        setIsRefreshingAttributeValues(true);
        try {
            const results = await Promise.all(
                attrScopes.map(scope => {
                    const scopeIndex = scopes.findIndex(
                        f => f?.kg_column?.header?.id === scope.kg_column?.header?.id
                    );
                    return fetchAttributeValues(scope, scopeIndex, scopes).then(result => ({
                        id: scope.kg_column?.header?.id,
                        ...result,
                    }));
                })
            );

            const refreshedScopes = scopes.map(f => {
                const result = results.find(r => r.id === f.kg_column?.header?.id);
                if (!result) return f;
                if (result.vals === null) {
                    // Keep existing scope unchanged on error
                    return f;
                }
                const newAvailable = result.vals;
                const cleanedValues = (f.values ?? []).filter(v => newAvailable.includes(v));
                return ColumnScopeProto.create({
                    kg_column: f.kg_column,
                    operator: f.operator,
                    values: cleanedValues,
                    is_hidden: f.is_hidden,
                    available_values: newAvailable,
                });
            });

            setColumnScopes(refreshedScopes);
            setAttributeValueErrors(prev => {
                const next = new Map(prev);
                results.forEach(r => {
                    if (!r.id) return;
                    if (r.vals === null) next.set(r.id, r.errors);
                    else next.delete(r.id);
                });
                return next;
            });
            setAttributesNeedRefresh(false);
            return refreshedScopes;
        } finally {
            setIsRefreshingAttributeValues(false);
        }
    };

    // Always keep the ref up-to-date with the latest version of this function.
    // Without this, the parent would call a stale version that has old columnScopes captured in its closure.
    useEffect(() => {
        if (refreshAttributeValuesRef) {
            refreshAttributeValuesRef.current = onRefreshAttributeValues;
        }
    });

    // ─── Add all handlers ─────────────────────────────────────────────────────────
    const onAddAllMetrics = async () => {
        const toAdd = listOfMetrics.filter(m => !selectedMetricHeaders.some(s => s.id === m.id));
        if (toAdd.length === 0) return;

        toAdd.forEach(m => { if (m.id) markNew(m.id); });

        // Separate into cached (edges already fetched) and needs-fetch
        const cached = toAdd
            .filter(m => m.id && fetchedEdgesForMetricIdsRef.current.has(m.id))
            .map(m => metricProtoMap.get(m.id!))
            .filter((m): m is MetricProto => !!m);
        const toFetch = toAdd.filter(m => m.id && !fetchedEdgesForMetricIdsRef.current.has(m.id));

        if (cached.length > 0) {
            setSelectedMetricsWithEdges(prev => [...prev, ...cached]);
        }

        if (toFetch.length === 0) return;

        const ids = toFetch.map(m => m.id).filter((id): id is string => !!id);
        try {
            const results = await getAllMetrics(ids, true);
            ids.forEach(id => fetchedEdgesForMetricIdsRef.current.add(id));
            setMetricProtoMap(prev => {
                const next = new Map(prev);
                results.forEach(m => { if (m.header?.id) next.set(m.header.id, m); });
                return next;
            });
            setSelectedMetricsWithEdges(prev => [...prev, ...results]);
        } catch {
            showToast("Failed to fetch Metrics", "", "error");
            toFetch.forEach(m => { if (m.id) unmarkNew(m.id); });
        }
    };

    const onRemoveAllMetrics = () => {
        setSelectedMetricsWithEdges([]);
    };

    const onAddAllRelatedEntities = () => {
        attributeList.filter(h => !selectedAttributes.some(s => s.id === h.id)).forEach(toggleAttributeSelection);
        measureList.filter(h => !selectedMeasures.some(s => s.id === h.id)).forEach(toggleMeasureSelection);
        timeframeList.filter(h => !selectedTimeframes.some(s => s.id === h.id)).forEach(toggleTimeframeSelection);
        benchmarkList.filter(h => !selectedBenchmarks.some(s => s.id === h.id)).forEach(toggleBenchmarkSelection);
    };

    const onRemoveAllRelatedEntities = () => {
        setSelectedBenchmarks([]);
        setColumnScopes([]);
        setAttributesNeedRefresh(false);
    };

    return (
        <Box
            w="100%"
            flex="1"
            minH={0}
            minW={0}
            display="flex"
            flexDirection="column"
            alignItems="center"
        >
            <VStack
                align="stretch"
                spacing={4}
                flex="1"
                minH={0}
                minW={0}
                w="100%"
                maxW={{ base: "100%", md: "90%", lg: "85%" }}
                pb={4}
            >
                <Flex
                    flex="1"
                    minH={0}
                    borderWidth="1px"
                    borderColor="gray.400"
                    borderRadius="md"
                    overflow="hidden"
                    bg="white"
                    boxShadow="md"
                >
                    <DataScopeLeftPanel
                        listOfMetrics={listOfMetrics}
                        isLoadingMetrics={isLoadingMetrics}
                        isLoadingRelatedEntities={isLoadingRelatedEntities}
                        selectedMetricHeaders={selectedMetricHeaders}
                        toggleMetricSelection={toggleMetricSelection}
                        onAddAllMetrics={onAddAllMetrics}
                        onRemoveAllMetrics={onRemoveAllMetrics}
                        attributeList={attributeList}
                        selectedAttributes={selectedAttributes}
                        toggleAttributeSelection={toggleAttributeSelection}
                        measureList={measureList}
                        selectedMeasures={selectedMeasures}
                        toggleMeasureSelection={toggleMeasureSelection}
                        timeframeList={timeframeList}
                        selectedTimeframes={selectedTimeframes}
                        toggleTimeframeSelection={toggleTimeframeSelection}
                        benchmarkList={benchmarkList}
                        selectedBenchmarks={selectedBenchmarks}
                        toggleBenchmarkSelection={toggleBenchmarkSelection}
                        onAddAllRelatedEntities={onAddAllRelatedEntities}
                        onRemoveAllRelatedEntities={onRemoveAllRelatedEntities}
                        isLocked={isRefreshingAttributeValues}
                        kgColumnProtoMap={kgColumnProtoMap}
                        dataModelList={dataModelList}
                        metricProtoMap={metricProtoMap}
                    />
                    <Flex direction="column" flex="0 0 65%" minW="280px" bg="gray.50" minH={0}>
                        <DataScopeRightPanel
                            isLoadingScope={isLoadingDataScope}
                            selectedMetricHeaders={selectedMetricHeaders}
                            onRemoveMetric={removeSelectedMetric}
                            selectedAttributes={selectedAttributes}
                            selectedMeasures={selectedMeasures}
                            selectedTimeframes={selectedTimeframes}
                            selectedBenchmarks={selectedBenchmarks}
                            onRemoveBenchmark={toggleBenchmarkSelection}
                            columnScopes={columnScopes}
                            sessionNewIds={sessionNewIds}
                            loadingAttributeIds={loadingAttributeIds}
                            onSaveConstraint={onSaveConstraint}
                            onRemoveColumnScope={removeColumnScope}
                            originalAnalyst={originalAnalyst}
                            isRefreshingAttributeValues={isRefreshingAttributeValues}
                            isSavingConstraint={isAutoSavingConstraint}
                            attributeValueErrors={attributeValueErrors}
                            kgColumnProtoMap={kgColumnProtoMap}
                            dataModelList={dataModelList}
                            metricProtoMap={metricProtoMap}
                        />
                    </Flex>
                </Flex>
            </VStack>
        </Box>
    );
};

export default AnalystDataScopeContainer;
